import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  Building2,
  Users,
  Globe,
  Target,
  ArrowRight,
  ArrowLeft,
  Check,
  Loader2,
  Palette,
  ExternalLink,
  MessageSquare,
  UserCheck,
  Award,
  Video,
  TrendingUp,
  Megaphone,
  Mail,
  HelpCircle,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Industry options
const INDUSTRIES = [
  'E-commerce',
  'SaaS / Technology',
  'Marketing Agency',
  'Consulting',
  'Healthcare',
  'Finance',
  'Education',
  'Real Estate',
  'Retail',
  'Manufacturing',
  'Non-profit',
  'Other',
];

// Team size options
const TEAM_SIZES = [
  { value: 'solo', label: 'Just me', icon: '👤' },
  { value: '2-5', label: '2-5 people', icon: '👥' },
  { value: '6-10', label: '6-10 people', icon: '👨‍👩‍👧‍👦' },
  { value: '11-50', label: '11-50 people', icon: '🏢' },
  { value: '51-200', label: '51-200 people', icon: '🏛️' },
  { value: '200+', label: '200+ people', icon: '🌆' },
];

// Revenue ranges
const REVENUE_RANGES = [
  { value: 'pre-revenue', label: 'Pre-revenue / Just starting' },
  { value: 'under-10k', label: 'Under $10k/month' },
  { value: '10k-50k', label: '$10k - $50k/month' },
  { value: '50k-100k', label: '$50k - $100k/month' },
  { value: '100k-500k', label: '$100k - $500k/month' },
  { value: '500k-1m', label: '$500k - $1M/month' },
  { value: '1m+', label: '$1M+/month' },
];

// Social platforms
const SOCIAL_PLATFORMS = [
  { id: 'instagram', name: 'Instagram', icon: '📸', color: 'from-pink-500 to-purple-500' },
  { id: 'facebook', name: 'Facebook', icon: '👍', color: 'from-blue-600 to-blue-500' },
  { id: 'twitter', name: 'X (Twitter)', icon: '🐦', color: 'from-gray-700 to-gray-900' },
  { id: 'linkedin', name: 'LinkedIn', icon: '💼', color: 'from-blue-700 to-blue-600' },
  { id: 'youtube', name: 'YouTube', icon: '📺', color: 'from-red-600 to-red-500' },
  { id: 'tiktok', name: 'TikTok', icon: '🎵', color: 'from-gray-900 to-pink-500' },
  { id: 'pinterest', name: 'Pinterest', icon: '📌', color: 'from-red-500 to-red-400' },
  { id: 'threads', name: 'Threads', icon: '🧵', color: 'from-gray-800 to-gray-700' },
];

// Goals
const GOALS = [
  { id: 'content', label: 'Create more content', icon: '✍️' },
  { id: 'social', label: 'Grow social media', icon: '📱' },
  { id: 'leads', label: 'Generate more leads', icon: '🎯' },
  { id: 'automate', label: 'Automate workflows', icon: '⚡' },
  { id: 'research', label: 'Research competitors', icon: '🔍' },
  { id: 'customer', label: 'Improve customer support', icon: '💬' },
  { id: 'seo', label: 'Improve SEO', icon: '📈' },
  { id: 'email', label: 'Email marketing', icon: '📧' },
];

// Client sources
const CLIENT_SOURCES = [
  { id: 'word-of-mouth', label: 'Word of mouth', icon: '🗣️' },
  { id: 'google-search', label: 'Google Search', icon: '🔍' },
  { id: 'paid-ads', label: 'Paid Ads', icon: '💰' },
  { id: 'organic-social', label: 'Organic social media', icon: '📱' },
  { id: 'event', label: 'Event', icon: '🎪' },
  { id: 'partnership', label: 'Partnership', icon: '🤝' },
  { id: 'others', label: 'Others', icon: '📋' },
  { id: 'no-client-yet', label: 'No client yet', icon: '🌱' },
];

// Online meeting frequency
const MEETING_FREQUENCIES = [
  { value: 'daily', label: 'Yes, almost daily', icon: '📅' },
  { value: 'few-times-week', label: 'A few times a week', icon: '📆' },
  { value: 'occasionally', label: 'Occasionally', icon: '🗓️' },
  { value: 'no', label: 'No', icon: '❌' },
];

// Biggest challenges
const CHALLENGES = [
  { id: 'lead-generation', label: 'Lead generation', icon: '🎯' },
  { id: 'content-creation', label: 'Content creation', icon: '✍️' },
  { id: 'customer-retention', label: 'Customer retention', icon: '🔄' },
  { id: 'time-management', label: 'Time management', icon: '⏰' },
  { id: 'scaling', label: 'Scaling operations', icon: '📈' },
  { id: 'brand-awareness', label: 'Brand awareness', icon: '📢' },
  { id: 'others', label: 'Others', icon: '💡' },
];

// Marketing handling options
const MARKETING_OPTIONS = [
  { value: 'in-house', label: 'In-house team', icon: '🏠' },
  { value: 'freelancers', label: 'Freelancers', icon: '👨‍💻' },
  { value: 'agency', label: 'Agency', icon: '🏢' },
  { value: 'myself', label: 'I do it myself', icon: '💪' },
  { value: 'not-yet', label: 'Not doing marketing yet', icon: '🚀' },
];

// Communication channels
const COMMUNICATION_CHANNELS = [
  { id: 'email', label: 'Email', icon: '📧' },
  { id: 'phone', label: 'Phone', icon: '📞' },
  { id: 'whatsapp', label: 'WhatsApp/SMS', icon: '💬' },
  { id: 'social-dms', label: 'Social media DMs', icon: '📱' },
  { id: 'live-chat', label: 'Live chat', icon: '🗨️' },
  { id: 'in-person', label: 'In-person', icon: '🤝' },
];

// Referral sources
const REFERRAL_SOURCES = [
  { value: 'google', label: 'Google Search', icon: '🔍' },
  { value: 'social-media', label: 'Social Media', icon: '📱' },
  { value: 'friend', label: 'Friend/Colleague', icon: '👥' },
  { value: 'youtube', label: 'YouTube', icon: '📺' },
  { value: 'blog', label: 'Blog/Article', icon: '📝' },
  { value: 'podcast', label: 'Podcast', icon: '🎙️' },
  { value: 'ad', label: 'Advertisement', icon: '📢' },
  { value: 'other', label: 'Other', icon: '💡' },
];

interface OnboardingData {
  // Step 1: Business Info
  businessName: string;
  industry: string;
  otherIndustry: string;
  // Step 2: Team & Revenue
  teamSize: string;
  monthlyRevenue: string;
  // Step 3: Social Platforms
  socialPlatforms: string[];
  // Step 4: Website & Brand
  websiteUrl: string;
  extractedLogo?: string;
  extractedFavicon?: string;
  brandColors?: string[];
  brandDescription?: string;
  metaTitle?: string;
  metaDescription?: string;
  // Step 5: Goals
  goals: string[];
  // Step 6: Client Sources
  clientSources: string[];
  otherClientSource: string;
  // Step 7: Business Description
  businessDescription: string;
  // Step 8: Ideal Customer
  idealCustomer: string;
  // Step 9: What Makes You Unique
  uniqueValue: string;
  // Step 10: Biggest Challenge
  biggestChallenge: string;
  otherChallenge: string;
  // Step 11: Marketing Handling
  marketingHandling: string;
  // Step 12: Communication Channels
  communicationChannels: string[];
  // Step 13: Online Meetings
  onlineMeetings: string;
  // Step 14: How Did You Hear
  referralSource: string;
  otherReferralSource: string;
}

const TOTAL_STEPS = 14;

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, token, updateUser, checkAuth } = useAuthStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionError, setExtractionError] = useState('');
  const [completionError, setCompletionError] = useState('');

  const [isAIGenerating, setIsAIGenerating] = useState(false);

  const [data, setData] = useState<OnboardingData>({
    businessName: '',
    industry: '',
    otherIndustry: '',
    teamSize: '',
    monthlyRevenue: '',
    socialPlatforms: [],
    websiteUrl: '',
    goals: [],
    clientSources: [],
    otherClientSource: '',
    businessDescription: '',
    idealCustomer: '',
    uniqueValue: '',
    biggestChallenge: '',
    otherChallenge: '',
    marketingHandling: '',
    communicationChannels: [],
    onlineMeetings: '',
    referralSource: '',
    otherReferralSource: '',
  });

  // Auto-fill business name from user's name if available (only on initial mount)
  useEffect(() => {
    if (user?.name) {
      setData(prev => {
        // Only set if business name is empty
        if (!prev.businessName) {
          return {
            ...prev,
            businessName: `${user.name.split(' ')[0]}'s Business`,
          };
        }
        return prev;
      });
    }
  }, [user?.name]);

  // Extract website data when URL is entered
  const extractWebsiteData = async () => {
    if (!data.websiteUrl) return;

    // Validate URL
    let url = data.websiteUrl.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }

    try {
      new URL(url);
    } catch {
      setExtractionError('Please enter a valid website URL');
      return;
    }

    setIsExtracting(true);
    setExtractionError('');

    try {
      const response = await fetch(`${API_URL}/onboarding/extract-website`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          url,
          analyzeWithAI: true,
          saveToKnowledge: true
        }),
      });

      interface ExtractResponse {
        success: boolean;
        data?: {
          logo?: string;
          favicon?: string;
          colors?: string[];
          description?: string;
          title?: string;
          metaDescription?: string;
          aiAnalysis?: {
            brandDescription?: string;
            brandVoice?: string;
            uniqueValueProposition?: string;
            targetAudience?: string;
            industryClassification?: string;
            keyServices?: string[];
            brandPersonality?: string;
          };
        };
        error?: string;
      }

      const result = await response.json() as ExtractResponse;

      if (result.success && result.data) {
        const aiData = result.data.aiAnalysis;
        setData(prev => ({
          ...prev,
          websiteUrl: url,
          extractedLogo: result.data?.logo,
          extractedFavicon: result.data?.favicon,
          brandColors: result.data?.colors,
          // Use AI-generated brand description if available, otherwise fall back to meta description
          brandDescription: aiData?.brandDescription || result.data?.description,
          metaTitle: result.data?.title,
          metaDescription: result.data?.metaDescription,
          // Auto-fill business name from meta title if empty
          businessName: prev.businessName || result.data?.title?.split(' - ')[0] || prev.businessName,
          // Auto-fill industry from AI analysis if available
          industry: prev.industry || aiData?.industryClassification || prev.industry,
        }));
      } else {
        setExtractionError(result.error || 'Could not extract website data');
      }
    } catch (error) {
      console.error('Website extraction error:', error);
      setExtractionError('Failed to connect to website');
    } finally {
      setIsExtracting(false);
    }
  };

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async (overrideReferralSource?: string) => {
    setIsLoading(true);

    // Use override if provided (for auto-complete), otherwise use data state
    const finalReferralSource = overrideReferralSource || (data.referralSource === 'other' ? data.otherReferralSource : data.referralSource);

    try {
      const response = await fetch(`${API_URL}/onboarding/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          businessName: data.businessName,
          websiteUrl: data.websiteUrl,
          industry: data.industry === 'Other' ? data.otherIndustry : data.industry,
          teamSize: data.teamSize,
          monthlyRevenue: data.monthlyRevenue,
          socialPlatforms: data.socialPlatforms,
          goals: data.goals,
          extractedLogo: data.extractedLogo,
          extractedFavicon: data.extractedFavicon,
          brandColors: data.brandColors,
          brandDescription: data.brandDescription,
          metaTitle: data.metaTitle,
          metaDescription: data.metaDescription,
          clientSources: data.clientSources,
          otherClientSource: data.clientSources.includes('others') ? data.otherClientSource : undefined,
          businessDescription: data.businessDescription,
          idealCustomer: data.idealCustomer,
          uniqueValue: data.uniqueValue,
          biggestChallenge: data.biggestChallenge === 'others' ? data.otherChallenge : data.biggestChallenge,
          marketingHandling: data.marketingHandling,
          communicationChannels: data.communicationChannels,
          onlineMeetings: data.onlineMeetings,
          referralSource: finalReferralSource,
        }),
      });

      interface CompleteResponse {
        success: boolean;
        error?: string;
      }

      const result = await response.json() as CompleteResponse;

      if (result.success) {
        // Update user state to mark onboarding as completed
        updateUser({ onboarding_completed: 1 });
        // Refresh user data from server to ensure consistency
        await checkAuth();
        navigate('/dashboard');
      } else {
        setCompletionError(result.error || 'Failed to complete onboarding. Please try again.');
      }
    } catch (error) {
      console.error('Onboarding error:', error);
      setCompletionError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleArrayItem = (field: keyof OnboardingData, itemId: string) => {
    setData(prev => {
      const currentArray = prev[field] as string[];
      return {
        ...prev,
        [field]: currentArray.includes(itemId)
          ? currentArray.filter(item => item !== itemId)
          : [...currentArray, itemId],
      };
    });
  };

  // Generate text with AI for textarea steps
  const generateWithAI = async (step: 7 | 8 | 9) => {
    setIsAIGenerating(true);
    try {
      const response = await fetch(`${API_URL}/onboarding/generate-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          step,
          businessName: data.businessName,
          industry: data.industry === 'Other' ? data.otherIndustry : data.industry,
          teamSize: data.teamSize,
          monthlyRevenue: data.monthlyRevenue,
          socialPlatforms: data.socialPlatforms,
          websiteUrl: data.websiteUrl,
          goals: data.goals,
          clientSources: data.clientSources,
          brandDescription: data.brandDescription,
          businessDescription: data.businessDescription,
          idealCustomer: data.idealCustomer,
          uniqueValue: data.uniqueValue,
        }),
      });

      interface GenerateResponse {
        success: boolean;
        data?: { text: string };
        error?: string;
      }

      const result = await response.json() as GenerateResponse;

      if (result.success && result.data?.text) {
        const fieldMap: Record<number, keyof OnboardingData> = {
          7: 'businessDescription',
          8: 'idealCustomer',
          9: 'uniqueValue',
        };
        setData(prev => ({ ...prev, [fieldMap[step]]: result.data!.text }));
      }
    } catch (error) {
      console.error('AI generation error:', error);
    } finally {
      setIsAIGenerating(false);
    }
  };

  // Get suggestion chips for textarea steps based on previous data
  const getSuggestionChips = (step: 7 | 8 | 9): string[] => {
    const industry = data.industry === 'Other' ? (data.otherIndustry || 'our industry') : (data.industry || 'our industry');
    const name = data.businessName || 'Our company';

    // Team size label
    const teamSizeLabels: Record<string, string> = {
      'solo': 'just myself', '2-5': 'a small team of 2-5 people', '6-10': 'a team of 6-10 people',
      '11-50': 'a growing team of 11-50 people', '51-200': 'a team of 51-200 people', '200+': 'over 200 team members',
    };
    const teamSize = teamSizeLabels[data.teamSize] || 'our team';

    // Revenue label
    const revenueLabels: Record<string, string> = {
      'pre-revenue': 'just starting out', 'under-10k': 'generating under $10k monthly',
      '10k-50k': 'generating $10k-$50k monthly', '50k-100k': 'generating $50k-$100k monthly',
      '100k-500k': 'generating $100k-$500k monthly', '500k-1m': 'generating $500k-$1M monthly', '1m+': 'generating over $1M monthly',
    };
    const revenue = revenueLabels[data.monthlyRevenue] || '';

    // Goals
    const goalLabels: Record<string, string> = {
      content: 'content creation', social: 'social media growth', leads: 'lead generation',
      automate: 'workflow automation', research: 'competitor research', customer: 'customer support',
      seo: 'SEO improvement', email: 'email marketing',
    };
    const topGoals = data.goals.slice(0, 2).map(g => goalLabels[g] || g).join(' and ');

    // Client sources
    const sourceLabels: Record<string, string> = {
      'word-of-mouth': 'word of mouth', 'google-search': 'Google Search', 'paid-ads': 'paid advertising',
      'organic-social': 'organic social media', 'event': 'events', 'partnership': 'partnerships',
    };
    const topSources = data.clientSources.filter(s => s !== 'others' && s !== 'no-client-yet').slice(0, 2).map(s => sourceLabels[s] || s).join(' and ');

    if (step === 7) {
      return [
        `We are a ${industry.toLowerCase()} company with ${teamSize}${revenue ? ', ' + revenue : ''}. We specialize in ${topGoals || 'delivering high-quality solutions'} for our clients.`,
        `${name} is a ${industry.toLowerCase()} business focused on helping companies with ${topGoals || 'their business goals'}. ${topSources ? 'Our clients typically find us through ' + topSources + '.' : ''}`,
        `We provide comprehensive ${industry.toLowerCase()} services with ${teamSize}. Our focus is on ${topGoals || 'delivering measurable results'} for businesses like yours.`,
      ];
    }
    if (step === 8) {
      return [
        `Our ideal customers are small to medium business owners in the ${industry.toLowerCase()} space who need help with ${topGoals || 'growing their business'}.`,
        `We serve entrepreneurs and business leaders who struggle with ${topGoals || 'scaling their operations'} and want a dedicated partner to help them succeed.`,
        `Our target audience includes companies in the ${industry.toLowerCase()} sector${topSources ? ' who typically find us through ' + topSources : ''}, seeking to improve their results.`,
      ];
    }
    // step === 9
    return [
      `What sets us apart is our personalized approach to ${topGoals || 'client success'}, backed by ${teamSize} dedicated to delivering exceptional results.`,
      `We combine deep ${industry.toLowerCase()} expertise with innovative strategies that consistently outperform industry standards for our clients.`,
      `Our unique value is the hands-on attention we provide${revenue ? ', despite ' + revenue : ''}, ensuring every client receives tailored solutions that drive real growth.`,
    ];
  };

  // Auto-advance to next step after a brief delay for visual feedback
  const autoAdvance = (delay: number = 350) => {
    setTimeout(() => {
      if (currentStep < TOTAL_STEPS) {
        setCurrentStep(prev => prev + 1);
      }
    }, delay);
  };

  // Determine if Continue button should be shown (hidden on auto-advance steps)
  const shouldShowContinueButton = () => {
    // Step 1: Hide if industry is selected and not "Other" (auto-advances)
    if (currentStep === 1 && data.industry && data.industry !== 'Other') {
      return false;
    }
    // Step 2: Hide (auto-advances after both selections)
    if (currentStep === 2) {
      return false;
    }
    // Step 10: Hide if challenge is selected and not "others" (auto-advances)
    if (currentStep === 10 && data.biggestChallenge && data.biggestChallenge !== 'others') {
      return false;
    }
    // Step 11: Hide (auto-advances on selection)
    if (currentStep === 11) {
      return false;
    }
    // Step 13: Hide (auto-advances on selection)
    if (currentStep === 13) {
      return false;
    }
    return true;
  };

  // Determine if Complete Setup button should be shown on last step
  const shouldShowCompleteButton = () => {
    // Step 14: Hide if referralSource is selected and not "other" (auto-completes)
    if (data.referralSource && data.referralSource !== 'other') {
      return false;
    }
    return true;
  };

  // Check if current step is valid to proceed - ALL FIELDS REQUIRED
  const canProceed = () => {
    switch (currentStep) {
      case 1: // Business Info
        if (data.industry === 'Other') {
          return data.businessName.trim().length > 0 && data.otherIndustry.trim().length > 0;
        }
        return data.businessName.trim().length > 0 && data.industry.length > 0;
      case 2: // Team & Revenue
        return data.teamSize.length > 0 && data.monthlyRevenue.length > 0;
      case 3: // Social Platforms
        return data.socialPlatforms.length > 0;
      case 4: // Website & Brand - OPTIONAL (not everyone has a website)
        return true; // Always allow proceeding, website is optional
      case 5: // Goals
        return data.goals.length > 0;
      case 6: // Client Sources
        if (data.clientSources.includes('others')) {
          return data.clientSources.length > 0 && data.otherClientSource.trim().length > 0;
        }
        return data.clientSources.length > 0;
      case 7: // Business Description
        return data.businessDescription.trim().length >= 20;
      case 8: // Ideal Customer
        return data.idealCustomer.trim().length >= 20;
      case 9: // Unique Value
        return data.uniqueValue.trim().length >= 20;
      case 10: // Biggest Challenge
        if (data.biggestChallenge === 'others') {
          return data.otherChallenge.trim().length > 0;
        }
        return data.biggestChallenge.length > 0;
      case 11: // Marketing Handling
        return data.marketingHandling.length > 0;
      case 12: // Communication Channels
        return data.communicationChannels.length > 0;
      case 13: // Online Meetings
        return data.onlineMeetings.length > 0;
      case 14: // Referral Source
        if (data.referralSource === 'other') {
          return data.otherReferralSource.trim().length > 0;
        }
        return data.referralSource.length > 0;
      default:
        return false;
    }
  };

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0,
    }),
  };

  return (
    <div className={`h-screen overflow-hidden flex flex-col ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
      {/* Header */}
      <div className={`flex-shrink-0 px-4 py-2 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <img
            src={isDark ? '/logos/logo-5.png' : '/logos/logo-6.png'}
            alt="SweetPilot"
            className="w-28 sm:w-36 object-contain"
          />
        </div>
      </div>

      {/* Progress Bar */}
      <div className={`flex-shrink-0 px-4 py-2 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-0.5">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <div key={i} className={`flex-1 h-1 rounded-full overflow-hidden ${isDark ? 'bg-gray-700/30' : 'bg-slate-300/50'}`}>
                <motion.div
                  className="h-full bg-gradient-to-r from-purple-500 to-cyan-500"
                  initial={{ width: 0 }}
                  animate={{ width: i < currentStep ? '100%' : '0%' }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            ))}
          </div>
          <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
            Step {currentStep} of {TOTAL_STEPS}
          </p>
        </div>
      </div>

      {/* Main Content - Scrollable area */}
      <div className="flex-1 overflow-y-auto px-4">
        <div className="max-w-2xl mx-auto py-2">
          <AnimatePresence mode="wait" custom={currentStep}>
            {/* Step 1: Business Info */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                custom={1}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-cyan-500/20 flex items-center justify-center mx-auto mb-2">
                    <Building2 className="w-5 h-5 text-purple-400" />
                  </div>
                  <h1 className={`text-lg sm:text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Tell us about your business
                  </h1>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                    This helps us personalize your AI employees
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                      Business Name
                    </label>
                    <input
                      type="text"
                      value={data.businessName}
                      onChange={(e) => setData({ ...data, businessName: e.target.value })}
                      onBlur={(e) => {
                        // Auto-advance on blur if both fields are filled and industry is not "Other"
                        if (e.target.value.trim().length > 0 && data.industry && data.industry !== 'Other') {
                          autoAdvance();
                        }
                      }}
                      placeholder="Enter your business name"
                      className={`w-full px-4 py-3 rounded-xl border transition-colors ${
                        isDark
                          ? 'bg-dark-800 border-white/10 text-white placeholder-gray-500 focus:border-purple-500'
                          : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-purple-500'
                      }`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                      Industry
                    </label>
                    <select
                      value={data.industry}
                      onChange={(e) => {
                        const newIndustry = e.target.value;
                        setData({ ...data, industry: newIndustry, otherIndustry: newIndustry !== 'Other' ? '' : data.otherIndustry });
                        // Auto-advance if business name is filled and industry is not "Other"
                        if (newIndustry && newIndustry !== 'Other' && data.businessName.trim().length > 0) {
                          autoAdvance();
                        }
                      }}
                      className={`w-full px-4 py-3 rounded-xl border transition-colors ${
                        isDark
                          ? 'bg-dark-800 border-white/10 text-white focus:border-purple-500'
                          : 'bg-white border-slate-300 text-slate-900 focus:border-purple-500'
                      }`}
                    >
                      <option value="">Select your industry</option>
                      {INDUSTRIES.map((ind) => (
                        <option key={ind} value={ind}>{ind}</option>
                      ))}
                    </select>

                    {data.industry === 'Other' && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-3"
                      >
                        <input
                          type="text"
                          value={data.otherIndustry}
                          onChange={(e) => setData({ ...data, otherIndustry: e.target.value })}
                          placeholder="Please specify your industry"
                          className={`w-full px-4 py-3 rounded-xl border transition-colors ${
                            isDark
                              ? 'bg-dark-800 border-white/10 text-white placeholder-gray-500 focus:border-purple-500'
                              : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-purple-500'
                          }`}
                        />
                      </motion.div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Team & Revenue */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                custom={1}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center mx-auto mb-2">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                  <h1 className={`text-lg sm:text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Your team size
                  </h1>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                    This helps us recommend the right AI employees
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-2 mb-4">
                  {TEAM_SIZES.map((size) => (
                    <button
                      key={size.value}
                      onClick={() => {
                        setData({ ...data, teamSize: size.value });
                        // Auto-advance if monthly revenue is already selected
                        if (data.monthlyRevenue) {
                          autoAdvance();
                        }
                      }}
                      className={`p-2 rounded-lg border text-center transition-all ${
                        data.teamSize === size.value
                          ? 'bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border-purple-500'
                          : isDark
                          ? 'bg-dark-800 border-white/10 hover:border-white/30'
                          : 'bg-white border-slate-200 hover:border-slate-400'
                      }`}
                    >
                      <span className="text-lg block">{size.icon}</span>
                      <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {size.label}
                      </span>
                    </button>
                  ))}
                </div>

                <div>
                  <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                    Monthly Revenue
                  </label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {REVENUE_RANGES.map((range) => (
                      <button
                        key={range.value}
                        onClick={() => {
                          setData({ ...data, monthlyRevenue: range.value });
                          // Auto-advance if team size is already selected
                          if (data.teamSize) {
                            autoAdvance();
                          }
                        }}
                        className={`p-2 rounded-lg border text-left transition-all ${
                          data.monthlyRevenue === range.value
                            ? 'bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border-purple-500'
                            : isDark
                            ? 'bg-dark-800 border-white/10 hover:border-white/30'
                            : 'bg-white border-slate-200 hover:border-slate-400'
                        }`}
                      >
                        <span className={`text-xs ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {range.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Social Platforms */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                custom={1}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500/20 to-purple-500/20 flex items-center justify-center mx-auto mb-2">
                    <Globe className="w-5 h-5 text-pink-400" />
                  </div>
                  <h1 className={`text-lg sm:text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Where do you post content?
                  </h1>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                    Select all the platforms you use
                  </p>
                </div>

                <div className="mb-2">
                  <button
                    onClick={() => {
                      const allIds = SOCIAL_PLATFORMS.map(p => p.id);
                      const allSelected = allIds.every(id => data.socialPlatforms.includes(id));
                      if (!allSelected) {
                        setData(prev => ({ ...prev, socialPlatforms: [...allIds] }));
                        autoAdvance();
                      } else {
                        setData(prev => ({ ...prev, socialPlatforms: [] }));
                      }
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-all text-xs font-medium ${
                      SOCIAL_PLATFORMS.every(p => data.socialPlatforms.includes(p.id))
                        ? 'bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border-purple-500'
                        : isDark
                        ? 'bg-dark-800 border-white/10 hover:border-white/30'
                        : 'bg-white border-slate-200 hover:border-slate-400'
                    } ${isDark ? 'text-white' : 'text-slate-900'}`}
                  >
                    <div className={`w-3 h-3 rounded border flex items-center justify-center ${
                      SOCIAL_PLATFORMS.every(p => data.socialPlatforms.includes(p.id))
                        ? 'bg-purple-500 border-purple-500'
                        : isDark ? 'border-gray-500' : 'border-slate-400'
                    }`}>
                      {SOCIAL_PLATFORMS.every(p => data.socialPlatforms.includes(p.id)) && (
                        <Check className="w-2 h-2 text-white" />
                      )}
                    </div>
                    Select All
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {SOCIAL_PLATFORMS.map((platform) => (
                    <button
                      key={platform.id}
                      onClick={() => toggleArrayItem('socialPlatforms', platform.id)}
                      className={`p-2 rounded-lg border transition-all flex items-center gap-2 ${
                        data.socialPlatforms.includes(platform.id)
                          ? `bg-gradient-to-r ${platform.color} border-transparent text-white`
                          : isDark
                          ? 'bg-dark-800 border-white/10 hover:border-white/30'
                          : 'bg-white border-slate-200 hover:border-slate-400'
                      }`}
                    >
                      <span className="text-base">{platform.icon}</span>
                      <span className={`text-xs font-medium ${
                        data.socialPlatforms.includes(platform.id)
                          ? 'text-white'
                          : isDark ? 'text-white' : 'text-slate-900'
                      }`}>
                        {platform.name}
                      </span>
                      {data.socialPlatforms.includes(platform.id) && (
                        <Check className="w-3 h-3 ml-auto" />
                      )}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 4: Website URL & Brand Extraction */}
            {currentStep === 4 && (
              <motion.div
                key="step4"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                custom={1}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500/20 to-pink-500/20 flex items-center justify-center mx-auto mb-2">
                    <Palette className="w-5 h-5 text-orange-400" />
                  </div>
                  <h1 className={`text-lg sm:text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Let's grab your brand assets
                  </h1>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                    Enter your website to extract logo, colors & description
                  </p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                    This step is optional
                  </p>
                </div>

                <div className="space-y-2">
                  <div>
                    <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                      Website URL
                    </label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="url"
                        value={data.websiteUrl}
                        onChange={(e) => setData({ ...data, websiteUrl: e.target.value })}
                        placeholder="https://yourwebsite.com"
                        className={`flex-1 px-3 py-2 rounded-lg border text-sm transition-colors ${
                          isDark
                            ? 'bg-dark-800 border-white/10 text-white placeholder-gray-500 focus:border-purple-500'
                            : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-purple-500'
                        }`}
                      />
                      <button
                        onClick={extractWebsiteData}
                        disabled={isExtracting || !data.websiteUrl}
                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-cyan-500 text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-all flex items-center justify-center gap-1.5"
                      >
                        {isExtracting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <ExternalLink className="w-4 h-4" />
                        )}
                        Extract
                      </button>
                    </div>
                    {extractionError && (
                      <p className="text-red-400 text-xs mt-1">{extractionError}</p>
                    )}
                  </div>

                  {/* Extracted Data Preview */}
                  {(data.extractedLogo || data.brandColors?.length || data.brandDescription) && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-3 rounded-lg border ${isDark ? 'bg-dark-800 border-white/10' : 'bg-white border-slate-200'}`}
                    >
                      <h3 className={`text-sm font-semibold mb-2 flex items-center gap-1.5 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        <Check className="w-4 h-4 text-green-500" />
                        Extracted Brand Assets
                      </h3>

                      <div className="space-y-2">
                        {data.extractedLogo && (
                          <div className="flex items-center gap-2">
                            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Logo:</span>
                            <img
                              src={data.extractedLogo}
                              alt="Logo"
                              className="h-8 object-contain rounded"
                            />
                          </div>
                        )}

                        {data.brandColors && data.brandColors.length > 0 && (
                          <div className="flex items-center gap-2">
                            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Colors:</span>
                            <div className="flex gap-1">
                              {data.brandColors.map((color, i) => (
                                <div
                                  key={i}
                                  className="w-6 h-6 rounded border border-white/20"
                                  style={{ backgroundColor: color }}
                                  title={color}
                                />
                              ))}
                            </div>
                          </div>
                        )}

                        {data.brandDescription && (
                          <div>
                            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Description:</span>
                            <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                              {data.brandDescription}
                            </p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Step 5: Goals */}
            {currentStep === 5 && (
              <motion.div
                key="step5"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                custom={1}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center mx-auto mb-2">
                    <Target className="w-5 h-5 text-green-400" />
                  </div>
                  <h1 className={`text-lg sm:text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    What are your main goals?
                  </h1>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                    We'll suggest the best AI employees for you
                  </p>
                </div>

                <div className="mb-2">
                  <button
                    onClick={() => {
                      const allIds = GOALS.map(g => g.id);
                      const allSelected = allIds.every(id => data.goals.includes(id));
                      if (!allSelected) {
                        setData(prev => ({ ...prev, goals: [...allIds] }));
                        autoAdvance();
                      } else {
                        setData(prev => ({ ...prev, goals: [] }));
                      }
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-all text-xs font-medium ${
                      GOALS.every(g => data.goals.includes(g.id))
                        ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500'
                        : isDark
                        ? 'bg-dark-800 border-white/10 hover:border-white/30'
                        : 'bg-white border-slate-200 hover:border-slate-400'
                    } ${isDark ? 'text-white' : 'text-slate-900'}`}
                  >
                    <div className={`w-3 h-3 rounded border flex items-center justify-center ${
                      GOALS.every(g => data.goals.includes(g.id))
                        ? 'bg-green-500 border-green-500'
                        : isDark ? 'border-gray-500' : 'border-slate-400'
                    }`}>
                      {GOALS.every(g => data.goals.includes(g.id)) && (
                        <Check className="w-2 h-2 text-white" />
                      )}
                    </div>
                    Select All
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {GOALS.map((goal) => (
                    <button
                      key={goal.id}
                      onClick={() => toggleArrayItem('goals', goal.id)}
                      className={`relative p-2 rounded-lg border transition-all text-left flex items-center gap-2 ${
                        data.goals.includes(goal.id)
                          ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500'
                          : isDark
                          ? 'bg-dark-800 border-white/10 hover:border-white/30'
                          : 'bg-white border-slate-200 hover:border-slate-400'
                      }`}
                    >
                      <span className="text-base">{goal.icon}</span>
                      <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {goal.label}
                      </span>
                      {data.goals.includes(goal.id) && (
                        <Check className="w-3 h-3 text-green-500 ml-auto" />
                      )}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 6: Client Sources */}
            {currentStep === 6 && (
              <motion.div
                key="step6"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                custom={1}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center mx-auto mb-2">
                    <UserCheck className="w-5 h-5 text-yellow-400" />
                  </div>
                  <h1 className={`text-lg sm:text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Where do your clients come from?
                  </h1>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                    Select all that apply
                  </p>
                </div>

                <div className="mb-2">
                  <button
                    onClick={() => {
                      const allIdsExceptOthers = CLIENT_SOURCES.filter(s => s.id !== 'others').map(s => s.id);
                      const allSelected = allIdsExceptOthers.every(id => data.clientSources.includes(id));
                      if (!allSelected) {
                        setData(prev => ({ ...prev, clientSources: [...allIdsExceptOthers], otherClientSource: '' }));
                        autoAdvance();
                      } else {
                        setData(prev => ({ ...prev, clientSources: [], otherClientSource: '' }));
                      }
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-all text-xs font-medium ${
                      CLIENT_SOURCES.filter(s => s.id !== 'others').every(s => data.clientSources.includes(s.id))
                        ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-500'
                        : isDark
                        ? 'bg-dark-800 border-white/10 hover:border-white/30'
                        : 'bg-white border-slate-200 hover:border-slate-400'
                    } ${isDark ? 'text-white' : 'text-slate-900'}`}
                  >
                    <div className={`w-3 h-3 rounded border flex items-center justify-center ${
                      CLIENT_SOURCES.filter(s => s.id !== 'others').every(s => data.clientSources.includes(s.id))
                        ? 'bg-yellow-500 border-yellow-500'
                        : isDark ? 'border-gray-500' : 'border-slate-400'
                    }`}>
                      {CLIENT_SOURCES.filter(s => s.id !== 'others').every(s => data.clientSources.includes(s.id)) && (
                        <Check className="w-2 h-2 text-white" />
                      )}
                    </div>
                    Select All
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {CLIENT_SOURCES.map((source) => (
                    <button
                      key={source.id}
                      onClick={() => toggleArrayItem('clientSources', source.id)}
                      className={`relative p-2 rounded-lg border transition-all text-left flex items-center gap-2 ${
                        data.clientSources.includes(source.id)
                          ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-500'
                          : isDark
                          ? 'bg-dark-800 border-white/10 hover:border-white/30'
                          : 'bg-white border-slate-200 hover:border-slate-400'
                      }`}
                    >
                      <span className="text-base">{source.icon}</span>
                      <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {source.label}
                      </span>
                      {data.clientSources.includes(source.id) && (
                        <Check className="w-3 h-3 text-yellow-500 ml-auto" />
                      )}
                    </button>
                  ))}
                </div>

                {data.clientSources.includes('others') && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-2"
                  >
                    <input
                      type="text"
                      value={data.otherClientSource}
                      onChange={(e) => setData({ ...data, otherClientSource: e.target.value })}
                      placeholder="Please specify your other client source"
                      className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors ${
                        isDark
                          ? 'bg-dark-800 border-white/10 text-white placeholder-gray-500 focus:border-purple-500'
                          : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-purple-500'
                      }`}
                    />
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* Step 7: Business Description */}
            {currentStep === 7 && (
              <motion.div
                key="step7"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                custom={1}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center mx-auto mb-2">
                    <MessageSquare className="w-5 h-5 text-indigo-400" />
                  </div>
                  <h1 className={`text-lg sm:text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Tell us about your business
                  </h1>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                    Brief summary of what your company does
                  </p>
                </div>

                <div className="relative">
                  <textarea
                    value={data.businessDescription}
                    onChange={(e) => setData({ ...data, businessDescription: e.target.value })}
                    placeholder="Describe your business, products, and services..."
                    rows={3}
                    className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors resize-none ${
                      isDark
                        ? 'bg-dark-800 border-white/10 text-white placeholder-gray-500 focus:border-purple-500'
                        : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-purple-500'
                    }`}
                  />
                  <div className="flex items-center justify-between mt-1">
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                      {data.businessDescription.length}/20 min
                    </p>
                    <button
                      onClick={() => generateWithAI(7)}
                      disabled={isAIGenerating}
                      className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gradient-to-r from-purple-500 to-cyan-500 text-white text-xs font-medium hover:opacity-90 disabled:opacity-50 transition-all"
                    >
                      {isAIGenerating ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Sparkles className="w-3 h-3" />
                      )}
                      AI Generate
                    </button>
                  </div>

                  {/* Suggestion Chips */}
                  <div className="mt-2">
                    <p className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                      Quick suggestions:
                    </p>
                    <div className="flex flex-col gap-1">
                      {getSuggestionChips(7).map((chip, i) => (
                        <button
                          key={i}
                          onClick={() => setData(prev => ({ ...prev, businessDescription: chip }))}
                          className={`text-left px-2 py-1.5 rounded-lg border text-xs transition-all line-clamp-2 ${
                            isDark
                              ? 'bg-dark-800/50 border-white/5 text-gray-400 hover:border-purple-500/50 hover:text-gray-300'
                              : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-purple-400 hover:text-slate-800'
                          }`}
                        >
                          {chip}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 8: Ideal Customer */}
            {currentStep === 8 && (
              <motion.div
                key="step8"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                custom={1}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center mx-auto mb-2">
                    <Users className="w-5 h-5 text-cyan-400" />
                  </div>
                  <h1 className={`text-lg sm:text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Who is your ideal customer?
                  </h1>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                    Describe your target audience and pain points
                  </p>
                </div>

                <div className="relative">
                  <textarea
                    value={data.idealCustomer}
                    onChange={(e) => setData({ ...data, idealCustomer: e.target.value })}
                    placeholder="Describe your ideal customer, their demographics, needs, and challenges..."
                    rows={3}
                    className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors resize-none ${
                      isDark
                        ? 'bg-dark-800 border-white/10 text-white placeholder-gray-500 focus:border-purple-500'
                        : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-purple-500'
                    }`}
                  />
                  <div className="flex items-center justify-between mt-1">
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                      {data.idealCustomer.length}/20 min
                    </p>
                    <button
                      onClick={() => generateWithAI(8)}
                      disabled={isAIGenerating}
                      className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gradient-to-r from-purple-500 to-cyan-500 text-white text-xs font-medium hover:opacity-90 disabled:opacity-50 transition-all"
                    >
                      {isAIGenerating ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Sparkles className="w-3 h-3" />
                      )}
                      AI Generate
                    </button>
                  </div>

                  {/* Suggestion Chips */}
                  <div className="mt-2">
                    <p className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                      Quick suggestions:
                    </p>
                    <div className="flex flex-col gap-1">
                      {getSuggestionChips(8).map((chip, i) => (
                        <button
                          key={i}
                          onClick={() => setData(prev => ({ ...prev, idealCustomer: chip }))}
                          className={`text-left px-2 py-1.5 rounded-lg border text-xs transition-all line-clamp-2 ${
                            isDark
                              ? 'bg-dark-800/50 border-white/5 text-gray-400 hover:border-cyan-500/50 hover:text-gray-300'
                              : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-cyan-400 hover:text-slate-800'
                          }`}
                        >
                          {chip}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 9: What Makes You Unique */}
            {currentStep === 9 && (
              <motion.div
                key="step9"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                custom={1}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 to-yellow-500/20 flex items-center justify-center mx-auto mb-2">
                    <Award className="w-5 h-5 text-amber-400" />
                  </div>
                  <h1 className={`text-lg sm:text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    What makes you unique?
                  </h1>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                    Share your competitive advantage
                  </p>
                </div>

                <div className="relative">
                  <textarea
                    value={data.uniqueValue}
                    onChange={(e) => setData({ ...data, uniqueValue: e.target.value })}
                    placeholder="What makes your business stand out? What's your unique value proposition?"
                    rows={3}
                    className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors resize-none ${
                      isDark
                        ? 'bg-dark-800 border-white/10 text-white placeholder-gray-500 focus:border-purple-500'
                        : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-purple-500'
                    }`}
                  />
                  <div className="flex items-center justify-between mt-1">
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                      {data.uniqueValue.length}/20 min
                    </p>
                    <button
                      onClick={() => generateWithAI(9)}
                      disabled={isAIGenerating}
                      className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gradient-to-r from-purple-500 to-cyan-500 text-white text-xs font-medium hover:opacity-90 disabled:opacity-50 transition-all"
                    >
                      {isAIGenerating ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Sparkles className="w-3 h-3" />
                      )}
                      AI Generate
                    </button>
                  </div>

                  {/* Suggestion Chips */}
                  <div className="mt-2">
                    <p className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                      Quick suggestions:
                    </p>
                    <div className="flex flex-col gap-1">
                      {getSuggestionChips(9).map((chip, i) => (
                        <button
                          key={i}
                          onClick={() => setData(prev => ({ ...prev, uniqueValue: chip }))}
                          className={`text-left px-2 py-1.5 rounded-lg border text-xs transition-all line-clamp-2 ${
                            isDark
                              ? 'bg-dark-800/50 border-white/5 text-gray-400 hover:border-amber-500/50 hover:text-gray-300'
                              : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-amber-400 hover:text-slate-800'
                          }`}
                        >
                          {chip}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 10: Biggest Challenge */}
            {currentStep === 10 && (
              <motion.div
                key="step10"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                custom={1}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500/20 to-orange-500/20 flex items-center justify-center mx-auto mb-2">
                    <TrendingUp className="w-5 h-5 text-red-400" />
                  </div>
                  <h1 className={`text-lg sm:text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    What's your biggest challenge?
                  </h1>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                    Help us understand where you need support
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {CHALLENGES.map((challenge) => (
                    <button
                      key={challenge.id}
                      onClick={() => {
                        setData({ ...data, biggestChallenge: challenge.id, otherChallenge: challenge.id !== 'others' ? '' : data.otherChallenge });
                        // Auto-advance only if not "Others" (which requires text input)
                        if (challenge.id !== 'others') {
                          autoAdvance();
                        }
                      }}
                      className={`relative p-2 rounded-lg border transition-all text-left flex items-center gap-2 ${
                        data.biggestChallenge === challenge.id
                          ? 'bg-gradient-to-r from-red-500/20 to-orange-500/20 border-red-500'
                          : isDark
                          ? 'bg-dark-800 border-white/10 hover:border-white/30'
                          : 'bg-white border-slate-200 hover:border-slate-400'
                      }`}
                    >
                      <span className="text-base">{challenge.icon}</span>
                      <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {challenge.label}
                      </span>
                      {data.biggestChallenge === challenge.id && (
                        <Check className="w-3 h-3 text-red-500 ml-auto" />
                      )}
                    </button>
                  ))}
                </div>

                {data.biggestChallenge === 'others' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-2"
                  >
                    <input
                      type="text"
                      value={data.otherChallenge}
                      onChange={(e) => setData({ ...data, otherChallenge: e.target.value })}
                      placeholder="Please describe your biggest challenge"
                      className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors ${
                        isDark
                          ? 'bg-dark-800 border-white/10 text-white placeholder-gray-500 focus:border-purple-500'
                          : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-purple-500'
                      }`}
                    />
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* Step 11: Marketing Handling */}
            {currentStep === 11 && (
              <motion.div
                key="step11"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                custom={1}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500/20 to-pink-500/20 flex items-center justify-center mx-auto mb-2">
                    <Megaphone className="w-5 h-5 text-fuchsia-400" />
                  </div>
                  <h1 className={`text-lg sm:text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    How do you handle marketing?
                  </h1>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                    Tell us about your current setup
                  </p>
                </div>

                <div className="space-y-1.5">
                  {MARKETING_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setData({ ...data, marketingHandling: option.value });
                        // Auto-advance on selection
                        autoAdvance();
                      }}
                      className={`w-full p-2 rounded-lg border transition-all flex items-center gap-2 ${
                        data.marketingHandling === option.value
                          ? 'bg-gradient-to-r from-fuchsia-500/20 to-pink-500/20 border-fuchsia-500'
                          : isDark
                          ? 'bg-dark-800 border-white/10 hover:border-white/30'
                          : 'bg-white border-slate-200 hover:border-slate-400'
                      }`}
                    >
                      <span className="text-base">{option.icon}</span>
                      <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {option.label}
                      </span>
                      {data.marketingHandling === option.value && (
                        <Check className="w-3 h-3 text-fuchsia-500 ml-auto" />
                      )}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 12: Communication Channels */}
            {currentStep === 12 && (
              <motion.div
                key="step12"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                custom={1}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500/20 to-cyan-500/20 flex items-center justify-center mx-auto mb-2">
                    <Mail className="w-5 h-5 text-teal-400" />
                  </div>
                  <h1 className={`text-lg sm:text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    How do you communicate with customers?
                  </h1>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                    Select all channels you use
                  </p>
                </div>

                <div className="mb-2">
                  <button
                    onClick={() => {
                      const allIds = COMMUNICATION_CHANNELS.map(c => c.id);
                      const allSelected = allIds.every(id => data.communicationChannels.includes(id));
                      if (!allSelected) {
                        // Select all and auto-advance
                        setData(prev => ({ ...prev, communicationChannels: [...allIds] }));
                        autoAdvance();
                      } else {
                        // Deselect all (no auto-advance)
                        setData(prev => ({ ...prev, communicationChannels: [] }));
                      }
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-all text-xs font-medium ${
                      COMMUNICATION_CHANNELS.every(c => data.communicationChannels.includes(c.id))
                        ? 'bg-gradient-to-r from-teal-500/20 to-cyan-500/20 border-teal-500'
                        : isDark
                        ? 'bg-dark-800 border-white/10 hover:border-white/30'
                        : 'bg-white border-slate-200 hover:border-slate-400'
                    } ${isDark ? 'text-white' : 'text-slate-900'}`}
                  >
                    <div className={`w-3 h-3 rounded border flex items-center justify-center ${
                      COMMUNICATION_CHANNELS.every(c => data.communicationChannels.includes(c.id))
                        ? 'bg-teal-500 border-teal-500'
                        : isDark ? 'border-gray-500' : 'border-slate-400'
                    }`}>
                      {COMMUNICATION_CHANNELS.every(c => data.communicationChannels.includes(c.id)) && (
                        <Check className="w-2 h-2 text-white" />
                      )}
                    </div>
                    Select All
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {COMMUNICATION_CHANNELS.map((channel) => (
                    <button
                      key={channel.id}
                      onClick={() => toggleArrayItem('communicationChannels', channel.id)}
                      className={`relative p-2 rounded-lg border transition-all text-left flex items-center gap-2 ${
                        data.communicationChannels.includes(channel.id)
                          ? 'bg-gradient-to-r from-teal-500/20 to-cyan-500/20 border-teal-500'
                          : isDark
                          ? 'bg-dark-800 border-white/10 hover:border-white/30'
                          : 'bg-white border-slate-200 hover:border-slate-400'
                      }`}
                    >
                      <span className="text-base">{channel.icon}</span>
                      <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {channel.label}
                      </span>
                      {data.communicationChannels.includes(channel.id) && (
                        <Check className="w-3 h-3 text-teal-500 ml-auto" />
                      )}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 13: Online Meetings */}
            {currentStep === 13 && (
              <motion.div
                key="step13"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                custom={1}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center mx-auto mb-2">
                    <Video className="w-5 h-5 text-violet-400" />
                  </div>
                  <h1 className={`text-lg sm:text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Do you attend online meetings?
                  </h1>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                    This helps us recommend features
                  </p>
                </div>

                <div className="space-y-1.5">
                  {MEETING_FREQUENCIES.map((freq) => (
                    <button
                      key={freq.value}
                      onClick={() => {
                        setData({ ...data, onlineMeetings: freq.value });
                        // Auto-advance on selection
                        autoAdvance();
                      }}
                      className={`w-full p-2 rounded-lg border transition-all flex items-center gap-2 ${
                        data.onlineMeetings === freq.value
                          ? 'bg-gradient-to-r from-violet-500/20 to-purple-500/20 border-violet-500'
                          : isDark
                          ? 'bg-dark-800 border-white/10 hover:border-white/30'
                          : 'bg-white border-slate-200 hover:border-slate-400'
                      }`}
                    >
                      <span className="text-base">{freq.icon}</span>
                      <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {freq.label}
                      </span>
                      {data.onlineMeetings === freq.value && (
                        <Check className="w-3 h-3 text-violet-500 ml-auto" />
                      )}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 14: How Did You Hear */}
            {currentStep === 14 && (
              <motion.div
                key="step14"
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                custom={1}
                transition={{ duration: 0.3 }}
              >
                <div className="text-center mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/20 to-green-500/20 flex items-center justify-center mx-auto mb-2">
                    <HelpCircle className="w-5 h-5 text-emerald-400" />
                  </div>
                  <h1 className={`text-lg sm:text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    How did you hear about us?
                  </h1>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                    This helps us improve our reach
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {REFERRAL_SOURCES.map((source) => (
                    <button
                      key={source.value}
                      onClick={() => {
                        setData({ ...data, referralSource: source.value, otherReferralSource: source.value !== 'other' ? '' : data.otherReferralSource });
                        // Auto-complete onboarding if not "other" (which requires text input)
                        if (source.value !== 'other') {
                          setTimeout(() => {
                            handleComplete(source.value);
                          }, 350);
                        }
                      }}
                      disabled={isLoading}
                      className={`relative p-2 rounded-lg border transition-all text-left flex items-center gap-2 ${
                        data.referralSource === source.value
                          ? 'bg-gradient-to-r from-emerald-500/20 to-green-500/20 border-emerald-500'
                          : isDark
                          ? 'bg-dark-800 border-white/10 hover:border-white/30'
                          : 'bg-white border-slate-200 hover:border-slate-400'
                      } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className="text-base">{source.icon}</span>
                      <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {source.label}
                      </span>
                      {data.referralSource === source.value && (
                        <Check className="w-3 h-3 text-emerald-500 ml-auto" />
                      )}
                    </button>
                  ))}
                </div>

                {data.referralSource === 'other' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-2"
                  >
                    <input
                      type="text"
                      value={data.otherReferralSource}
                      onChange={(e) => setData({ ...data, otherReferralSource: e.target.value })}
                      placeholder="Please tell us where you heard about us"
                      className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors ${
                        isDark
                          ? 'bg-dark-800 border-white/10 text-white placeholder-gray-500 focus:border-purple-500'
                          : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-purple-500'
                      }`}
                    />
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className={`flex-shrink-0 px-4 py-2 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm transition-all ${
              currentStep === 1
                ? 'opacity-0 pointer-events-none'
                : isDark
                ? 'bg-white/10 text-white hover:bg-white/20'
                : 'bg-slate-200 text-slate-900 hover:bg-slate-300'
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="flex flex-col items-end gap-1">
            {completionError && currentStep === TOTAL_STEPS && (
              <p className="text-red-400 text-xs">{completionError}</p>
            )}
            {currentStep < TOTAL_STEPS ? (
              shouldShowContinueButton() && (
                <button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="flex items-center gap-1 px-5 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-cyan-500 text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-all"
                >
                  Continue
                  <ArrowRight className="w-4 h-4" />
                </button>
              )
            ) : (
              shouldShowCompleteButton() && (
                <button
                  onClick={() => {
                    setCompletionError('');
                    handleComplete();
                  }}
                  disabled={isLoading || !canProceed()}
                  className="flex items-center gap-1 px-5 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-all"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Complete Setup
                    </>
                  )}
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
